import { useUserContext } from '@vitvio/user-context';
import { formatInTimezone, parseISOSafe, type Dayjs } from '@vitvio/helpers';
import { CoreText, type FontToken, type SemanticColorToken, type PaddingProps } from '@vitvio/design-system';

interface TimeProps extends PaddingProps {
  date?: Date | Dayjs | string | null;
  showSeconds?: boolean;
  font?: FontToken;
  color?: SemanticColorToken;
  strikeThrough?: boolean;
}

const PLACEHOLDER = '--:--';

/**
 * The Time component handles displaying a given Date as time based
 * on the timezone of the active hospital. Date should be passed in as UTC.
 * When no valid date is provided, displays "--:--" placeholder.
 */
export function Time({ date, showSeconds, font, color, strikeThrough, ...paddingProps }: TimeProps) {
  const { timeFormat, hospitalTimezone } = useUserContext();

  const displayMode = timeFormat === '12hr' ? 'h:mm a' : `HH:mm${showSeconds ? ':ss' : ''}`;

  const adjustedTime = date ? formatInTimezone(date, hospitalTimezone, displayMode) : PLACEHOLDER;

  return (
    <CoreText font={font} color={color} strikeThrough={strikeThrough} {...paddingProps}>
      {adjustedTime}
    </CoreText>
  );
}

interface DateProps {
  dateString: string;
  formatString?: string;
}

export function FormattedDate({ dateString, formatString = 'ddd, DD MMM, YYYY' }: DateProps) {
  const { hospitalTimezone } = useUserContext();
  const date = parseISOSafe(dateString);
  return date ? formatInTimezone(date, hospitalTimezone, formatString) : '';
}
